import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPen,
  PdfColor,
  PdfPoint,
  PdfRect,
  PdfFont,
  PdfBrush,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutBaseGen } from '../layoutBase';
import { OptsBase } from '../optsBase';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/cut';
import { GUtil } from '../../pdf/gutil';
import { Random } from '@educraft/base';
import { GLabel, GTuple } from '../../pdf/glabel';
import {
  ELogicoVariant,
  ELogicoMode,
  genMap,
  drawLogico,
  drawLogicoCircle,
} from '../../pdf/board/logico';
import type { LogicoData } from '../../pdf/board/logico';
import { drawRelBoxes, Rel, RelRect } from '../../pdf/board/rel';

// ===== Local helper types and functions =====

enum EPiccoloBackground {
  None = 0,
  Normal = 1,
  Highlight = 2,
}

interface PiccoloBox {
  content?: string;
  piccoloIndex?: number;
  background?: EPiccoloBackground;
  outline?: boolean;
  stringFormat?: ETextAlign;
}

function drawPiccoloBox(
  g: PageGen,
  item: Rel<PiccoloBox>,
  rect: PdfRect,
  variant: ELogicoVariant = ELogicoVariant.Piccolo
): void {
  const {data} = item;

  switch (data.background) {
    case EPiccoloBackground.Normal:
      g.drawRectangle(null, new PdfBrush(PdfColor.fromRgb(211, 211, 211)), rect);
      break;
    case EPiccoloBackground.Highlight:
      g.drawRectangle(null, new PdfBrush(PdfColor.fromRgb(135, 206, 250)), rect);
      break;
  }

  if (data.piccoloIndex !== undefined) {
    drawLogicoCircle(g, variant, data.piccoloIndex, rect);
  } else if (data.content) {
    const f = g.adjustFont(rect.width, [data.content], GUtil.baseFont);
    g.drawString(
      data.content,
      f,
      PdfBrush.Black,
      null,
      rect,
      data.stringFormat ?? ETextAlign.Center
    );
  }

  if (data.outline !== false) {
    g.drawRectangle(GUtil.basePen, null, rect);
  }
}

/**
 * Piccolo Tabulka 100 - Number table 1-100 with Piccolo markers
 */
export class Piccolo_Tabulka_100 implements ILayoutBaseGen {
  public async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    // Need 2 pages for front and back
    const page1 = pdf.addPage();
    const page2 = pdf.addPage();
    const pages = [page1, page2];

    const random = opts.data?.random ?? new Random();

    const all = Array.from({ length: 100 }, (_, i) => i + 1);
    const used = new Set([1, 10, 100]);
    const map = genMap(10, random);

    // Pick some numbers to show
    const newUsed = new Set([
      ...all
        .filter((i) => !used.has(i))
        .sort(() => random.nextDouble() - 0.5)
        .slice(0, 2 + random.next(0, 5)),
      ...used,
    ]);

    // Pick solutions
    const solutions = all
      .filter((i) => !newUsed.has(i))
      .sort(() => random.nextDouble() - 0.5)
      .slice(0, 10);

    const dictionary = new Map<number, number>();
    for (let index = 0; index < solutions.length; index++) {
      dictionary.set(solutions[index], map[index]);
    }

    const labels: GTuple[] = solutions.map((i) => ({
      left: GLabel.fromText(i.toString()),
      right: GLabel.fromText(i.toString()),
    }));

    const boxes1: Rel<PiccoloBox>[] = [];
    const boxes2: Rel<PiccoloBox>[] = [];

    for (let y = 0; y < 10; y++) {
      for (let x = 0; x < 10; x++) {
        const number = y * 10 + x + 1;
        const numberStr = number.toString();
        const rect = new RelRect(x, y, 1, 1);

        if (newUsed.has(number)) {
          boxes1.push(new Rel({ content: numberStr }, rect));
          boxes2.push(new Rel({ content: numberStr }, rect));
        } else if (dictionary.has(number)) {
          boxes1.push(new Rel({ piccoloIndex: dictionary.get(number) }, rect));
          boxes2.push(new Rel({ piccoloIndex: dictionary.get(number) }, rect));
        } else {
          boxes1.push(new Rel({}, rect));
          boxes2.push(new Rel({ content: numberStr }, rect));
        }
      }
    }

    const drawQuestion = (data: LogicoData) => {
      const boxes = data.isFront ? boxes1 : boxes2;
      drawRelBoxes(data.page, data.rect, boxes, (g, item, rect) => {
        drawPiccoloBox(g, item, rect, ELogicoVariant.Piccolo);
      });
    };

    drawLogico(pdf, ELogicoVariant.Piccolo, ELogicoMode.One, labels, map, {
      id: opts.data?.random ? 'Piccolo-100' : 'Piccolo-100',
      drawQuestion,
      drawOutline: false,
      drawCircle: false,
      data: opts.data,
    });
  }
}

/**
 * Base class for Piccolo operation tables (multiplication/division, addition/subtraction).
 * Matches C# Piccolo_Table_Operations: callback(n) returns rows of [a, b, result],
 * operators[i1 % 2] selects the operator per row. Layout shows two columns side-by-side.
 */
class Piccolo_Table_Operations implements ILayoutBaseGen {
  constructor(
    private readonly callback: (n: number) => number[][],
    private readonly operators: string[],
    private readonly genId: string
  ) {}

  private static pickRandom(
    random: Random,
    numbers: number[][],
    count = 10
  ): [number, number][] | null {
    const rowIndices = Array.from({ length: numbers.length }, (_, i) => i).sort(
      () => random.nextDouble() - 0.5
    );
    const used = new Set<number>();
    const result: [number, number][] = [];

    for (const i1 of rowIndices) {
      const colIndices = Array.from({ length: numbers[i1].length }, (_, i) => i).sort(
        () => random.nextDouble() - 0.5
      );
      for (const i2 of colIndices) {
        const n = numbers[i1][i2];
        if (!used.has(n)) {
          result.push([i1, i2]);
          used.add(n);
          if (result.length >= count) return result;
          break; // only one per row
        }
      }
    }
    return null;
  }

  public async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    pdf.addPage();
    pdf.addPage();

    const random = opts.data?.random ?? new Random();
    const map = genMap(10, random);

    const numbers: number[][] = [];
    for (let n = 1; n <= 10; n++) {
      numbers.push(...this.callback(n));
    }

    let picked: [number, number][] | null = null;
    for (let counter = 0; counter < 100 && !picked; counter++) {
      picked = Piccolo_Table_Operations.pickRandom(random, numbers);
    }
    if (!picked) throw new Error('Timeout generating table operations');

    const dictionary = new Map<string, number>();
    const labels: GTuple[] = [];

    for (let i = 0; i < picked.length; i++) {
      const [r, c] = picked[i];
      dictionary.set(`${r},${c}`, i);
      const [mr, mc] = picked[map[i]];
      labels.push({
        left: GLabel.fromText(numbers[mr][mc].toString()),
        right: GLabel.fromText(numbers[mr][mc].toString()),
      });
    }

    const boxes: Rel<PiccoloBox>[] = [];

    for (let i1 = 0; i1 < numbers.length; i1++) {
      const y = Math.floor(i1 / 2) * 1.5;
      const x1 = (i1 % 2) * 5;
      const op = this.operators[i1 % 2];

      for (let i2 = 0; i2 < numbers[i1].length; i2++) {
        const x = x1 + i2 * 1.5;
        const key = `${i1},${i2}`;
        const r = new RelRect(x, y, 1, 1.5);
        if (dictionary.has(key)) {
          boxes.push(new Rel({ piccoloIndex: dictionary.get(key)!, outline: false }, r));
        } else {
          boxes.push(new Rel({ content: numbers[i1][i2].toString(), outline: false }, r));
        }
      }
      boxes.push(new Rel({ content: op, outline: false }, new RelRect(x1 + 1, y, 0.5, 1.5)));
      boxes.push(new Rel({ content: '=', outline: false }, new RelRect(x1 + 2.5, y, 0.5, 1.5)));
    }

    const drawQuestion = (data: LogicoData) => {
      drawRelBoxes(data.page, data.rect, boxes, (g, item, rect) => {
        drawPiccoloBox(g, item, rect, ELogicoVariant.Piccolo);
      });
    };

    drawLogico(pdf, ELogicoVariant.Piccolo, ELogicoMode.One, labels, map, {
      id: this.genId,
      drawQuestion,
      drawOutline: false,
      drawCircle: false,
      data: opts.data,
    });
  }
}

export class Piccolo_Tabulka__Nasobeni1 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 1, i * 1],
        [i * 1, 1, i],
      ],
      ['·', ':'],
      'Piccolo-Nasobeni1'
    );
  }
}

export class Piccolo_Tabulka__Nasobeni2 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 2, i * 2],
        [i * 2, 2, i],
      ],
      ['·', ':'],
      'Piccolo-Nasobeni2'
    );
  }
}

export class Piccolo_Tabulka__Nasobeni3 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 3, i * 3],
        [i * 3, 3, i],
      ],
      ['·', ':'],
      'Piccolo-Nasobeni3'
    );
  }
}

export class Piccolo_Tabulka__Nasobeni4 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 4, i * 4],
        [i * 4, 4, i],
      ],
      ['·', ':'],
      'Piccolo-Nasobeni4'
    );
  }
}

export class Piccolo_Tabulka__Nasobeni5 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 5, i * 5],
        [i * 5, 5, i],
      ],
      ['·', ':'],
      'Piccolo-Nasobeni5'
    );
  }
}

export class Piccolo_Tabulka__Nasobeni6 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 6, i * 6],
        [i * 6, 6, i],
      ],
      ['·', ':'],
      'Piccolo-Nasobeni6'
    );
  }
}

export class Piccolo_Tabulka__Nasobeni7 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 7, i * 7],
        [i * 7, 7, i],
      ],
      ['·', ':'],
      'Piccolo-Nasobeni7'
    );
  }
}

export class Piccolo_Tabulka__Nasobeni8 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 8, i * 8],
        [i * 8, 8, i],
      ],
      ['·', ':'],
      'Piccolo-Nasobeni8'
    );
  }
}

export class Piccolo_Tabulka__Nasobeni9 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 9, i * 9],
        [i * 9, 9, i],
      ],
      ['·', ':'],
      'Piccolo-Nasobeni9'
    );
  }
}

export class Piccolo_Tabulka__Nasobeni10 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 10, i * 10],
        [i * 10, 10, i],
      ],
      ['·', ':'],
      'Piccolo-Nasobeni10'
    );
  }
}

export class Piccolo_Tabulka__Scitani1 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 1, i + 1],
        [i + 1, 1, i],
      ],
      ['+', '–'],
      'Piccolo-Scitani1'
    );
  }
}

export class Piccolo_Tabulka__Scitani2 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 2, i + 2],
        [i + 2, 2, i],
      ],
      ['+', '–'],
      'Piccolo-Scitani2'
    );
  }
}

export class Piccolo_Tabulka__Scitani3 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 3, i + 3],
        [i + 3, 3, i],
      ],
      ['+', '–'],
      'Piccolo-Scitani3'
    );
  }
}

export class Piccolo_Tabulka__Scitani4 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 4, i + 4],
        [i + 4, 4, i],
      ],
      ['+', '–'],
      'Piccolo-Scitani4'
    );
  }
}

export class Piccolo_Tabulka__Scitani5 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 5, i + 5],
        [i + 5, 5, i],
      ],
      ['+', '–'],
      'Piccolo-Scitani5'
    );
  }
}

export class Piccolo_Tabulka__Scitani6 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 6, i + 6],
        [i + 6, 6, i],
      ],
      ['+', '–'],
      'Piccolo-Scitani6'
    );
  }
}

export class Piccolo_Tabulka__Scitani7 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 7, i + 7],
        [i + 7, 7, i],
      ],
      ['+', '–'],
      'Piccolo-Scitani7'
    );
  }
}

export class Piccolo_Tabulka__Scitani8 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 8, i + 8],
        [i + 8, 8, i],
      ],
      ['+', '–'],
      'Piccolo-Scitani8'
    );
  }
}

export class Piccolo_Tabulka__Scitani9 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 9, i + 9],
        [i + 9, 9, i],
      ],
      ['+', '–'],
      'Piccolo-Scitani9'
    );
  }
}

export class Piccolo_Tabulka__Scitani10 extends Piccolo_Table_Operations {
  constructor() {
    super(
      (i) => [
        [i, 10, i + 10],
        [i + 10, 10, i],
      ],
      ['+', '–'],
      'Piccolo-Scitani10'
    );
  }
}
